<template>
  <app-content class="h-panel">
    <div class="h-panel-bar">
      <span class="h-panel-title">代账工作台</span>
      <div class="h-panel-right">
        <Button icon="h-icon-refresh" @click="refresh">刷新</Button>
        <Button color="primary" icon="fa fa-reply" @click="$router.push('/')">返回做账</Button>
      </div>
    </div>
    <Row type="flex" class="h-panel-body">
      <Cell :width="3">
        <div class="wb-menu">
          <div v-for="tab in tabs" :key="tab.key" class="wb-menu-item"
               :class="{current: currentTab === tab.key}" @click="currentTab = tab.key">
            <i :class="tab.icon"></i> {{ tab.title }}
          </div>
        </div>
      </Cell>
      <Cell :flex="1" class="wb-content">
        <!--首页-->
        <div v-show="currentTab === 'home'">
          <Row type="flex" :space-x="16">
            <Cell v-for="card in progressCards" :key="card.key" :flex="1">
              <div class="stat-card" :class="card.cls">
                <div class="stat-value">{{ card.value }}</div>
                <div class="stat-title">{{ card.title }}</div>
              </div>
            </Cell>
          </Row>
          <div class="h-panel-bar margin-top"><span class="h-panel-title">工作进度表（{{ period }}）</span></div>
          <Table :datas="declareList" :border="true" :loading="loading">
            <TableItem title="税种" prop="taxItem" :width="180"></TableItem>
            <TableItem title="所属期" :width="180">
              <template slot-scope="{data}">{{ data.periodStart }} ~ {{ data.periodEnd }}</template>
            </TableItem>
            <TableItem title="申报状态" :width="100" align="center">
              <template slot-scope="{data}">
                <span class="h-tag" :class="data.declareStatus === 1 ? 'h-tag-bg-green' : 'h-tag-bg-yellow'">
                  {{ declareStatusText(data.declareStatus) }}
                </span>
              </template>
            </TableItem>
            <TableItem title="缴款状态" :width="100" align="center">
              <template slot-scope="{data}">
                <span class="h-tag" :class="data.payStatus === 1 ? 'h-tag-bg-green' : 'h-tag-bg-yellow'">
                  {{ payStatusText(data.payStatus) }}
                </span>
              </template>
            </TableItem>
            <TableItem title="应补退税额" align="right">
              <template slot-scope="{data}">{{ data.taxAmount | numFormat }}</template>
            </TableItem>
          </Table>
        </div>

        <!--客户账套-->
        <div v-show="currentTab === 'accounts'">
          <Table :datas="accountSets" :border="true" :loading="loading">
            <TableItem title="单位名称" prop="companyName"></TableItem>
            <TableItem title="当前记账年月" :width="180" align="center">
              <template slot-scope="{data}">
                <span>{{ currentPeriod(data) }}</span>
                <span v-if="isCurrent(data)" class="h-tag h-tag-bg-blue wb-current">当前</span>
              </template>
            </TableItem>
            <TableItem title="启用年月" :width="120" align="center">
              <template slot-scope="{data}">{{ data.enableDate ? formatMonth(data.enableDate) : '-' }}</template>
            </TableItem>
            <TableItem title="状态" :width="90" align="center">
              <template slot-scope="{data}">{{ statusText(data.status) }}</template>
            </TableItem>
            <TableItem title="到期时间" :width="130" align="center">
              <template slot-scope="{data}">
                <span :class="{'wb-expired': isExpired(data)}">
                  {{ data.expireDate ? formatDate(data.expireDate) : '长期有效' }}
                </span>
              </template>
            </TableItem>
            <TableItem title="操作" :width="130" align="center">
              <div class="actions" slot-scope="{data}">
                <span v-if="!isCurrent(data)" @click="doAccount(data)">做账</span>
                <span v-else class="wb-action-disabled">做账</span>
                <span v-if="isCurrent(data)" @click="$router.push({name: 'Account'})">编辑</span>
              </div>
            </TableItem>
          </Table>
        </div>

        <!--票据采集-->
        <div v-show="currentTab === 'invoice'">
          <Row type="flex" :space-x="16">
            <Cell :flex="1">
              <div class="stat-card stat-green">
                <div class="stat-value">{{ invoiceStat.inCount }}</div>
                <div class="stat-title">本月进项发票张数</div>
              </div>
            </Cell>
            <Cell :flex="1">
              <div class="stat-card stat-cyan">
                <div class="stat-value">{{ invoiceStat.inAmount | numFormat }}</div>
                <div class="stat-title">本月进项发票金额</div>
              </div>
            </Cell>
            <Cell :flex="1">
              <div class="stat-card stat-blue">
                <div class="stat-value">{{ invoiceStat.outCount }}</div>
                <div class="stat-title">本月销项发票张数</div>
              </div>
            </Cell>
            <Cell :flex="1">
              <div class="stat-card stat-orange">
                <div class="stat-value">{{ invoiceStat.outAmount | numFormat }}</div>
                <div class="stat-title">本月销项发票金额</div>
              </div>
            </Cell>
          </Row>
          <div class="wb-tip margin-top">
            <i class="h-icon-info"></i>
            发票采集/银行流水/勾选认证的自动采集需配置电子税局开放平台授权，当前为手工台账模式，请到
            <router-link :to="{name: 'OutputInvoice'}">发票</router-link> 模块录入。
          </div>
        </div>

        <!--纳税申报-->
        <div v-show="currentTab === 'declare'">
          <div class="margin-bottom">
            <Button color="primary" icon="fa fa-bank" @click="$router.push('/tax/declare')">去申报管理</Button>
          </div>
          <Table :datas="declareList" :border="true" :loading="loading">
            <TableItem title="财税项目" prop="taxItem" :width="180"></TableItem>
            <TableItem title="所属期" :width="180">
              <template slot-scope="{data}">{{ data.periodStart }} ~ {{ data.periodEnd }}</template>
            </TableItem>
            <TableItem title="申报状态" :width="100" align="center">
              <template slot-scope="{data}">
                <span class="h-tag" :class="data.declareStatus === 1 ? 'h-tag-bg-green' : 'h-tag-bg-yellow'">
                  {{ declareStatusText(data.declareStatus) }}
                </span>
              </template>
            </TableItem>
            <TableItem title="缴款状态" :width="100" align="center">
              <template slot-scope="{data}">
                <span class="h-tag" :class="data.payStatus === 1 ? 'h-tag-bg-green' : 'h-tag-bg-yellow'">
                  {{ payStatusText(data.payStatus) }}
                </span>
              </template>
            </TableItem>
            <TableItem title="应补退税额" align="right">
              <template slot-scope="{data}">{{ data.taxAmount | numFormat }}</template>
            </TableItem>
          </Table>
        </div>

        <!--统计报表-->
        <div v-show="currentTab === 'report'">
          <div class="shortcut-grid">
            <div v-for="item in reportShortcuts" :key="item.name" class="shortcut-item"
                 @click="$router.push({name: item.name})">
              <i :class="item.icon"></i>
              <span>{{ item.title }}</span>
            </div>
          </div>
        </div>

        <!--基础设置-->
        <div v-show="currentTab === 'setting'">
          <div class="shortcut-grid">
            <div v-for="item in settingShortcuts" :key="item.name" class="shortcut-item"
                 @click="$router.push({name: item.name})">
              <i :class="item.icon"></i>
              <span>{{ item.title }}</span>
            </div>
          </div>
        </div>
      </Cell>
    </Row>
  </app-content>
</template>

<script>
import moment from 'moment';
import {mapState} from 'vuex';

export default {
  name: 'Workbench',
  data() {
    return {
      tabs: [
        {key: 'home', title: '首页', icon: 'h-icon-home'},
        {key: 'accounts', title: '客户账套', icon: 'fa fa-briefcase'},
        {key: 'invoice', title: '票据采集', icon: 'fa fa-file-text-o'},
        {key: 'declare', title: '纳税申报', icon: 'fa fa-bank'},
        {key: 'report', title: '统计报表', icon: 'fa fa-bar-chart'},
        {key: 'setting', title: '基础设置', icon: 'h-icon-setting'}
      ],
      currentTab: 'home',
      loading: false,
      progress: {
        invoice: 0,
        voucher: 0,
        declared: 0,
        paid: 0
      },
      declareList: [],
      accountSets: [],
      invoiceStat: {
        inCount: 0,
        inAmount: 0,
        outCount: 0,
        outAmount: 0
      },
      reportShortcuts: [
        {name: 'ReportList', title: '报表中心', icon: 'h-icon-list'},
        {name: 'SubjectBalance', title: '科目余额表', icon: 'fa fa-balance-scale'},
        {name: 'SubjectSummary', title: '科目汇总表', icon: 'fa fa-table'},
        {name: 'ReportTemplate', title: '报表模板', icon: 'h-icon-template'}
      ],
      settingShortcuts: [
        {name: 'Account', title: '账套', icon: 'fa fa-database'},
        {name: 'SetsSettings', title: '参数中心', icon: 'h-icon-setting'},
        {name: 'Subject', title: '会计科目', icon: 'fa fa-sitemap'},
        {name: 'Initial', title: '期初余额', icon: 'fa fa-calculator'},
        {name: 'Currency', title: '币别设置', icon: 'fa fa-money'},
        {name: 'VoucherWord', title: '凭证字', icon: 'fa fa-file-text'},
        {name: 'AssistingAccounting', title: '辅助核算', icon: 'fa fa-tags'},
        {name: 'Backup', title: '备份恢复', icon: 'h-icon-backup'}
      ]
    }
  },
  computed: {
    ...mapState(['currentAccountSets']),
    //统计期间以账套当前记账年月为准（客户可能滞后做账），无账套时退回系统当月
    period() {
      const date = this.currentAccountSets && this.currentAccountSets.currentAccountDate;
      return date ? moment(date).format('YYYY-MM') : moment().format('YYYY-MM');
    },
    progressCards() {
      return [
        {key: 'invoice', title: `理票（${this.period}发票数）`, value: this.progress.invoice, cls: 'stat-blue'},
        {key: 'voucher', title: '记账（本月凭证数）', value: this.progress.voucher, cls: 'stat-green'},
        {key: 'declared', title: '申报（本月已申报）', value: this.progress.declared, cls: 'stat-cyan'},
        {key: 'paid', title: '缴款（已缴款）', value: this.progress.paid, cls: 'stat-orange'}
      ];
    }
  },
  methods: {
    formatMonth(val) {
      return moment(val).format('YYYY年MM月');
    },
    currentPeriod(data) {
      if (data.currentPeriod) {
        return data.currentPeriod;
      }
      return data.currentAccountDate ? this.formatMonth(data.currentAccountDate) : '-';
    },
    statusText(status) {
      if (status === 1 || status === '1' || status === true) {
        return '正常';
      }
      if (status === 0 || status === '0' || status === false) {
        return '停用';
      }
      return '-';
    },
    declareStatusText(status) {
      return status === 1 ? '已申报' : '未申报';
    },
    payStatusText(status) {
      return status === 1 ? '已缴款' : '未缴款';
    },
    doAccount(data) {
      if (data.status === 0 || data.status === '0' || data.status === false) {
        this.$Message.error('该账套已停用，无法做账');
        return;
      }
      this.$Loading('正在切换，请稍后...');
      this.$api.common.changeAccountSets(data.id).then(() => {
        this.$Message('切换成功！');
        window.location.replace('/');
      }).catch(() => {
        //切换失败（已到期/无权限等）时关闭遮罩，避免整页被锁死
        this.$Loading.close();
      });
    },
    isCurrent(data) {
      return !!this.currentAccountSets && this.currentAccountSets.id === data.id;
    },
    isExpired(data) {
      return !!data.expireDate && moment(data.expireDate).endOf('day').isBefore(moment());
    },
    formatDate(val) {
      return moment(val).format('YYYY-MM-DD');
    },
    periodRange() {
      return {
        start: moment(this.period, 'YYYY-MM').startOf('month').format('YYYY-MM-DD'),
        end: moment(this.period, 'YYYY-MM').endOf('month').format('YYYY-MM-DD')
      };
    },
    loadInvoiceStat() {
      const range = this.periodRange();
      this.$api.invoice.invoice.summary({
        start: range.start,
        end: range.end,
        excludeStatus: 'VOID,RED_ALL'
      }).then(({data}) => {
        const stat = data || {};
        this.invoiceStat.inCount = Number(stat.inCount) || 0;
        this.invoiceStat.inAmount = Number(stat.inAmount) || 0;
        this.invoiceStat.outCount = Number(stat.outCount) || 0;
        this.invoiceStat.outAmount = Number(stat.outAmount) || 0;
        this.progress.invoice = this.invoiceStat.inCount + this.invoiceStat.outCount;
      }).catch(() => {
        this.progress.invoice = 0;
      });
    },
    loadProgress() {
      //记账进度复用首页凭证数接口，统计口径与首页一致（排除草稿与作废）
      this.$api.home.voucherCount().then(({data}) => {
        this.progress.voucher = Number(data) || 0;
      }).catch(() => {
        this.progress.voucher = 0;
      });
      this.$api.tax.declare.summary(this.periodRange()).then(({data}) => {
        this.progress.declared = (data && data.declaredCount) || 0;
        this.progress.paid = (data && data.paidCount) || 0;
      }).catch(() => {
      });
    },
    loadDeclareList() {
      this.loading = true;
      const params = Object.assign({page: 1, size: 50}, this.periodRange());
      this.$api.tax.declare.list(params).then(({data}) => {
        this.declareList = data.results || data.records || [];
      }).catch(() => {
        this.declareList = [];
        this.$Message.error('申报台账加载失败，请稍后重试');
      }).finally(() => this.loading = false);
    },
    loadAccountSets() {
      this.$api.setting.accountSets.list().then(({data}) => {
        this.accountSets = data || [];
      }).catch(() => {
        this.accountSets = [];
        this.$Message.error('客户账套加载失败，请稍后重试');
      });
    },
    refresh() {
      this.loadProgress();
      this.loadInvoiceStat();
      this.loadDeclareList();
      this.loadAccountSets();
    }
  },
  mounted() {
    this.refresh();
  }
};
</script>

<style lang="less" scoped>
.wb-menu {
  border-right: 1px solid #eee;
  min-height: 400px;

  .wb-menu-item {
    padding: 12px 20px;
    cursor: pointer;
    font-size: 14px;

    i {
      margin-right: 6px;
    }

    &:hover {
      color: @primary-color;
    }

    &.current {
      color: @primary-color;
      background: @hover-background-color;
      border-right: 2px solid @primary-color;
    }
  }
}

.wb-content {
  padding: 0 16px 16px;
}

.wb-current {
  margin-left: 6px;
}

.wb-action-disabled {
  color: #c5c8ce;
  cursor: not-allowed;
}

.wb-expired {
  color: #ed4014;
}

.stat-card {
  border-radius: 4px;
  padding: 16px 20px;
  color: #fff;

  .stat-value {
    font-size: 26px;
    font-weight: bold;
    line-height: 32px;
  }

  .stat-title {
    font-size: 13px;
    opacity: .9;
    margin-top: 4px;
  }
}

.stat-blue {
  background: #2db7f5;
}

.stat-green {
  background: #19be6b;
}

.stat-cyan {
  background: #17c3b2;
}

.stat-orange {
  background: #ff9900;
}

.wb-tip {
  padding: 30px 20px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #fafafa;
  color: #515a6e;
  font-size: 14px;
  line-height: 24px;

  i {
    color: #2db7f5;
    margin-right: 6px;
  }
}

.shortcut-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  padding: 10px 0;
}

.shortcut-item {
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #fff;
  padding: 24px 16px;
  text-align: center;
  cursor: pointer;
  transition: all .2s;

  i {
    display: block;
    font-size: 28px;
    color: #2db7f5;
    margin-bottom: 10px;
  }

  span {
    font-size: 14px;
    color: #333;
  }

  &:hover {
    border-color: #2db7f5;
    box-shadow: 0 2px 8px rgba(45, 183, 245, .2);
  }
}
</style>
